// Help menu — the "?" button in the top bar.
//
// One place to find help: What's new (product updates for the apps the
// org has), the documentation, the getting-started guide, keyboard
// shortcuts, Athena, a human at support, and the public status page.
// A dot on the button means there are updates the person hasn't seen.
//
// Self-contained: the shell mounts <HelpMenu /> and passes the two
// callbacks it owns (the shortcuts dialog and the Athena panel); items
// whose callback isn't given are left out rather than shown dead.

import { useId, useRef, useState, type ComponentType, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Activity,
  BookOpen,
  ChevronRight,
  CircleHelp,
  ExternalLink,
  Keyboard,
  LifeBuoy,
  Megaphone,
  Rocket,
  Sparkles,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@cyber-aware/ui';
import { SUPPORT_EMAIL, supportMailto } from '@cyber-aware/shared-types';
import { useProductUpdates } from '../../lib/productUpdates';
import { WhatsNewPanel } from './WhatsNewPanel';

const FLOATING_PANEL =
  'border-slate-200 bg-white shadow-xl ring-1 ring-slate-900/5 dark:border-slate-700 dark:bg-slate-900';

/** The public status page (pages/public/StatusPage, route /status). */
const STATUS_PATH = '/status';

/** Read once: ⌘ on Apple platforms, Ctrl elsewhere. */
const IS_MAC = typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);

// Same visual language as the Apps and Focus menus (operator review,
// 23-24 Oct: menus need hierarchy, not plain lists): a tinted icon
// badge, a bold name over a muted one-line description, a clear hover.
const ITEM =
  'group flex cursor-pointer items-start gap-3 rounded-lg px-2 py-2 text-left focus:bg-slate-50 data-[highlighted]:bg-slate-50 dark:focus:bg-slate-800/70 dark:data-[highlighted]:bg-slate-800/70';
const SECTION = 'px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground';

// Cool-family tints only (red/amber/green mean status elsewhere).
const TINT = {
  slate: 'bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-500/15 dark:text-slate-300 dark:ring-slate-400/20',
  violet: 'bg-violet-50 text-violet-600 ring-violet-100 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-400/20',
  purple: 'bg-purple-50 text-purple-600 ring-purple-100 dark:bg-purple-500/10 dark:text-purple-300 dark:ring-purple-400/20',
} as const;

function ItemBody({
  icon: Icon,
  tint,
  title,
  description,
  trailing,
}: {
  icon: ComponentType<{ className?: string }>;
  tint: keyof typeof TINT;
  title: string;
  description: ReactNode;
  trailing?: ReactNode;
}) {
  return (
    <>
      <span className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ${TINT[tint]}`}>
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-sm font-semibold text-foreground">{title}</span>
        <span className="truncate text-xs text-muted-foreground">{description}</span>
      </span>
      {trailing && <span className="mt-1.5 flex shrink-0 items-center gap-0.5">{trailing}</span>}
    </>
  );
}

function Kbd({ children }: { children: string }) {
  return (
    <kbd className="inline-flex min-w-[1.25rem] items-center justify-center rounded border border-slate-300 bg-slate-50 px-1 font-mono text-[11px] font-medium tracking-normal text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200">
      {children}
    </kbd>
  );
}

export interface HelpMenuProps {
  /** Opens the keyboard-shortcuts dialog. Omit to hide the item. */
  onOpenShortcuts?: () => void;
  /** Opens the Athena panel. Omit to hide the item. */
  onAskAthena?: () => void;
}

export function HelpMenu({ onOpenShortcuts, onAskAthena }: HelpMenuProps) {
  const { pathname } = useLocation();
  const { unseenCount, updates } = useProductUpdates();
  const latest = updates[0];
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  // Set when an item opens something that takes focus itself, so the
  // menu doesn't pull focus back to the button as it closes.
  const handingOffFocus = useRef(false);
  const hasUnseen = unseenCount > 0;
  const unseenId = useId();

  const handOff = (open: () => void) => () => {
    handingOffFocus.current = true;
    open();
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            ref={triggerRef}
            type="button"
            aria-label="Help"
            aria-haspopup="menu"
            aria-describedby={hasUnseen ? unseenId : undefined}
            className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <CircleHelp className="h-5 w-5" aria-hidden="true" />
            {hasUnseen && (
              <>
                <span
                  aria-hidden="true"
                  className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-indigo-600 ring-2 ring-white dark:bg-indigo-400 dark:ring-slate-900"
                />
                <span id={unseenId} className="sr-only">
                  new updates
                </span>
              </>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          sideOffset={6}
          collisionPadding={16}
          className={`w-[22rem] max-w-[calc(100vw-2rem)] rounded-xl p-2 ${FLOATING_PANEL}`}
          onCloseAutoFocus={(e) => {
            if (handingOffFocus.current) {
              e.preventDefault();
              handingOffFocus.current = false;
            }
          }}
        >
          <DropdownMenuLabel className="px-2 pb-2 pt-1.5">
            <span className="block text-sm font-semibold text-foreground">Help and resources</span>
            <span className="block text-xs font-normal text-muted-foreground">Learn what’s new, find answers or reach us.</span>
          </DropdownMenuLabel>
          {/* What's new leads as a card: it's the item that changes. */}
          <DropdownMenuItem
            onSelect={handOff(() => setWhatsNewOpen(true))}
            className="group mb-1 flex cursor-pointer items-start gap-3 rounded-xl border border-indigo-100 bg-gradient-to-br from-indigo-50 to-white p-3 focus:border-indigo-200 data-[highlighted]:border-indigo-200 data-[highlighted]:from-indigo-100/70 dark:border-indigo-400/20 dark:from-indigo-500/15 dark:to-slate-900 dark:data-[highlighted]:from-indigo-500/25"
          >
            {/* Light badge, like the other items: the card already marks
                What's new as the lead item, and the solid "N new" pill is
                what should catch the eye, only when there's news. */}
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 ring-1 ring-indigo-200 dark:bg-indigo-500/20 dark:text-indigo-300 dark:ring-indigo-400/30">
              <Megaphone className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="flex items-center gap-2">
                <span className="text-sm font-semibold text-foreground">What’s new</span>
                {hasUnseen && (
                  <span className="rounded-full bg-indigo-600 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white dark:bg-indigo-500">
                    {unseenCount} new
                  </span>
                )}
              </span>
              <span className="line-clamp-2 text-xs text-muted-foreground">
                {latest ? latest.title : 'Product updates for the apps you use.'}
              </span>
            </span>
            <ChevronRight className="mt-2 h-4 w-4 shrink-0 text-muted-foreground transition-transform group-data-[highlighted]:translate-x-0.5" aria-hidden="true" />
          </DropdownMenuItem>

          <div className={SECTION}>Learn</div>
          <DropdownMenuItem asChild className={ITEM}>
            <Link to="/help">
              <ItemBody icon={BookOpen} tint="slate" title="Documentation" description="Guides for every app and setting" />
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className={ITEM}>
            <Link to="/onboarding">
              <ItemBody icon={Rocket} tint="slate" title="Getting started" description="Finish setting up your organization" />
            </Link>
          </DropdownMenuItem>
          {onOpenShortcuts && (
            <DropdownMenuItem className={ITEM} onSelect={handOff(onOpenShortcuts)}>
              <ItemBody
                icon={Keyboard}
                tint="slate"
                title="Keyboard shortcuts"
                description="Move around without the mouse"
                trailing={<Kbd>?</Kbd>}
              />
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator className="my-1.5" />
          <div className={SECTION}>Get help</div>
          {onAskAthena && (
            <DropdownMenuItem className={ITEM} onSelect={handOff(onAskAthena)}>
              <ItemBody
                icon={Sparkles}
                tint="violet"
                title="Ask Athena"
                description="Your AI assistant, beside any page"
                trailing={
                  <>
                    <Kbd>{IS_MAC ? '⌘' : 'Ctrl'}</Kbd>
                    <Kbd>/</Kbd>
                  </>
                }
              />
            </DropdownMenuItem>
          )}
          <DropdownMenuItem asChild className={ITEM}>
            <a href={supportMailto({ topic: 'Help menu', pagePath: pathname })}>
              <ItemBody icon={LifeBuoy} tint="purple" title="Contact support" description={SUPPORT_EMAIL} />
            </a>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className={ITEM}>
            <a href={STATUS_PATH} target="_blank" rel="noreferrer">
              <ItemBody
                icon={Activity}
                tint="slate"
                title="System status"
                description="Check X-One is running normally"
                trailing={
                  <>
                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                    <span className="sr-only">(opens in a new tab)</span>
                  </>
                }
              />
            </a>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <WhatsNewPanel open={whatsNewOpen} onClose={() => setWhatsNewOpen(false)} returnFocusRef={triggerRef} />
    </>
  );
}
